import { useProductLibraryQuery } from '@partful/pages/product-library/hooks/useProductLibraryQuery';
import type { ProductAPIResponse } from '@partful/shared-types';
import { useProductInventoryQuery } from '@partful/shared/lib/hooks/useProductInventoryQuery';
import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { FullscreenLoadingSpinner } from 'src/components/Loading/FullscreenLoadingSpinner';
import { useDecodedParams } from 'src/hooks/useDecodedParams';
import { getProduct } from 'src/services/api';
import { flattenAndHash } from 'src/services/partUtils';
import { productInventoryEndpoints } from 'src/services/productInventory';
import { useTenant } from '../Tenant';
import { useProductsListQuery } from '../useProductsListQuery';
import { useUser } from '../User';
import flattenParts from './flattenParts';
import { Product } from './Product';

interface IProductParams {
  productId: string;
}

export { Product };

export const ProductContext = createContext({} as Product);

/* istanbul ignore next - covered by unit test that has been skipped */
export function ProductProvider({ children }: any) {
  const { enabledFeatures } = useTenant();

  /* istanbul ignore next */
  if (enabledFeatures.includes('configuration-management')) {
    return <ProductProviderV2>{children}</ProductProviderV2>;
  }

  return <ProductProviderV1>{children}</ProductProviderV1>;
}

/* istanbul ignore next - covered by unit test that has been skipped */
export function ProductProviderV1({ children }: any) {
  const {
    userDetails: { currency },
  } = useUser();
  const [chosenVariantNames, setChosenVariantNames] = useState<string[]>([]);

  const toggleVariantNames = (previouslySelected: string[], selected: string) =>
    setChosenVariantNames((oldNames) => {
      const withoutPreviouslySelected = arrayWithoutItems(oldNames, previouslySelected);
      return [...withoutPreviouslySelected, selected];
    });

  const { productId } = useDecodedParams<IProductParams>();
  const getProductList = useProductsListQuery();

  const currentProduct = getProductList.data?.find((product) => product.productId === productId);

  const productQuery = () => getProduct(productId);

  const { data: product, error } = useQuery<ProductAPIResponse | undefined>(
    ['product', currency, currentProduct],
    productQuery
  );

  const parts = product && product.bom ? flattenAndHash(product.bom) : [];
  /* istanbul ignore next */
  if (error) return <Redirect to="/product-library" />;

  if (product) {
    const hiddenVariantNames = product.bom
      ? flattenParts(product.bom)
          .map((optionGroup) => {
            const optionsNotChosenByTheUser = optionGroup.children.filter(
              (variant) => !chosenVariantNames.includes(variant.id)
            );
            if (optionsNotChosenByTheUser.length === optionGroup.children.length) {
              /* istanbul ignore next */
              if (
                optionGroup.children.some(
                  (c) => c.defaultVariant // default variant should be set in the blender content addon
                )
              ) {
                return optionGroup.children.filter((c) => !c.defaultVariant);
              }
              return filterOutFirst(optionGroup.children);
            }
            return optionsNotChosenByTheUser;
          })
          .flat()
          .map((node) => node.object3DName)
          .filter((item): item is string => !!item)
      : /* istanbul ignore next */ [];

    // useMemo can't be called conditionally, assuming it's better to only build product after it's bein fetched then to memoize it.
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    const values = new Product({ product, partPaths: parts, toggleVariantNames, hiddenVariantNames });
    return <ProductContext.Provider value={values}>{children} </ProductContext.Provider>;
  }
  return <FullscreenLoadingSpinner />;
}

/* istanbul ignore next */
export function ProductProviderV2({ children }: any) {
  const { accountId } = useTenant();
  const { productId } = useDecodedParams<IProductParams>();

  const {
    userDetails: { currency },
  } = useUser();
  const [chosenVariantNames, setChosenVariantNames] = useState<string[]>([]);
  /* istanbul ignore next - variant/hidden variant is covered in old world tests, future new world integration tests may get this coverage  */
  const toggleVariantNames = (previouslySelected: string[], selected: string) =>
    setChosenVariantNames((oldNames) => {
      const withoutPreviouslySelected = arrayWithoutItems(oldNames, previouslySelected);
      return [...withoutPreviouslySelected, selected];
    });

  const { configurations } = useProductLibraryQuery();

  const currentConfiguration = configurations?.find((configuration) => configuration.productId === productId);
  const productExternalID = currentConfiguration?.productExternalID;
  const configurationExternalID = currentConfiguration?.configurationExternalID;

  // base 64 encode the productExternalID and configurationExternalID to avoid issues with slashes in the URL
  const encodedProductExternalID = btoa(productExternalID ?? '');
  const encodedConfigurationExternalID = btoa(configurationExternalID ?? '');

  // call the productInventory API to get the product data, with the optional encoding param
  const partTreeRoute = `${productInventoryEndpoints.partTrees}/product/${encodedProductExternalID}/version/latest/configuration/${encodedConfigurationExternalID}/version/latest?encoding=base64`;
  const { data: product, isError } = useProductInventoryQuery<ProductAPIResponse>(
    partTreeRoute,
    { account: accountId },
    { queryKey: [currency, currentConfiguration], enabled: Boolean(productExternalID && configurationExternalID) }
  );

  const parts = product?.bom ? flattenAndHash(product.bom) : [];
  /* istanbul ignore next */
  if (isError) return <Redirect to="/product-library" />;

  if (product && productExternalID === productId) {
    /* istanbul ignore next - variant/hidden variant is covered in old world tests, future new world integration tests may get this coverage  */
    const hiddenVariantNames = product.bom
      ? flattenParts(product.bom)
          .map((optionGroup) => {
            const optionsNotChosenByTheUser = optionGroup.children.filter(
              (variant) => !chosenVariantNames.includes(variant.id)
            );
            if (optionsNotChosenByTheUser.length === optionGroup.children.length) {
              if (
                optionGroup.children.some(
                  (c) => c.defaultVariant // default variant should be set in the blender content addon
                )
              ) {
                return optionGroup.children.filter((c) => !c.defaultVariant);
              }
              return filterOutFirst(optionGroup.children);
            }
            return optionsNotChosenByTheUser;
          })
          .flat()
          .map((node) => node.object3DName)
          .filter((item): item is string => !!item)
      : [];

    // useMemo can't be called conditionally, assuming it's better to only build product after it's bein fetched then to memoize it.
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    const values = new Product({ product, partPaths: parts, toggleVariantNames, hiddenVariantNames });
    return <ProductContext.Provider value={values}>{children} </ProductContext.Provider>;
  }

  return <FullscreenLoadingSpinner />;
}

export const useProduct = (): Product => {
  const context = useContext(ProductContext);
  /* istanbul ignore next */
  if (context === undefined) {
    throw new Error('`useProduct` hook must be used within a `ProductProvider` component');
  }
  return context;
};

/* istanbul ignore next - covered by skipped unit test */
function filterOutFirst<Type>(array: Type[]) {
  return array.filter((_item, index) => index !== 0);
}

/* istanbul ignore next - covered by skipped unit test */
function arrayWithoutItems<Type>(arr: Type[], items: Type[]) {
  return arr.filter((item) => !items.includes(item));
}
